// Copyright (c) StrangeBee 2023

import { classList } from '@Helpers/className';
import { useLocalMessage } from '@Helpers/locale';
import { FormatDate } from '@Molecules/Format';
import RouteBuilder from '@Services/RouteBuilder';
import React, { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { LicenseDetailsOutput, Quotas } from 'thehive-sdk';

import './LicenseContent.scss';

interface LicenseContentProps {
    license?: LicenseDetailsOutput;
    plan?: string;
    className?: string;
    about?: boolean;
    quotas?: Quotas;
    notFound?: boolean;
}

const orderingForce = [
    'users.readonly',
    'users.normal',
    'users.service',
    'organisations',
    'dashboards',
    'case.template',
    'cluster.nodes',
    'misp',
    'cortex',
];

const fixLink: Record<string, string> = {
    'users.readonly': RouteBuilder.compute('administration.global-users'),
    'users.normal': RouteBuilder.compute('administration.global-users'),
    'users.service': RouteBuilder.compute('administration.global-users'),
    organisations: RouteBuilder.compute('administration.organizations'),
    misp: RouteBuilder.compute('administration.misp'),
    cortex: RouteBuilder.compute('administration.cortex'),
};

export const LicenseContent = ({
    license,
    plan,
    className,
    about,
    quotas,
    notFound = true,
}: LicenseContentProps): ReactElement => {
    const i = useLocalMessage();

    return (
        <div className={classList('license-content', className)}>
            {plan && (
                <>
                    <div className="license-content__label">{i('license.details.license-plan')}</div>
                    <span className="license-content__value">{plan}</span>
                </>
            )}

            {!about && (
                <>
                    <div className="license-content__label">{i('license.details.user')}</div>
                    <span className="license-content__value">{license?.customer}</span>
                </>
            )}

            {!about && !notFound && (
                <>
                    <div className="license-content__label">{i('license.details.creation-date')}</div>
                    <span className="license-content__value">
                        <FormatDate displayFrom date={license?._createdAt} withoutSuffix />
                    </span>
                </>
            )}

            {!about && !notFound && (
                <>
                    <div className="license-content__label">{i('license.details.valid-date')}</div>
                    <span className="license-content__value">
                        <FormatDate displayFrom date={license?.validFrom} withoutSuffix />
                    </span>
                </>
            )}

            {!notFound && (
                <>
                    <div className="license-content__label">{i('license.details.expiration')}</div>
                    <span className="license-content__value">
                        <FormatDate displayFrom date={license?.expiresAt} withoutSuffix />
                    </span>
                </>
            )}

            {!about && (
                <>
                    {quotas &&
                        Object.entries(quotas)
                            .sort(([aKey], [bKey]) => orderingForce.indexOf(aKey) - orderingForce.indexOf(bKey))
                            .map(([key, data]) => {
                                const isOverload = data.quota !== -1 && data.current > data.quota;
                                return (
                                    <React.Fragment key={key}>
                                        <span className="license-content__label">{i(`license.quotas.${key}`)}</span>
                                        <span
                                            className={classList(['license-content__value', 'licence-quota__list'], {
                                                overload: isOverload,
                                            })}
                                        >
                                            <span className="licence-quota__item quota-current">{data.current}</span>/
                                            <span className="licence-quota__item quota-quota">
                                                {data.quota === -1 ? i('license.quotas.unlimited') : data.quota}
                                            </span>
                                            {isOverload && fixLink[key] && (
                                                <Link
                                                    type="link"
                                                    to={fixLink[key]}
                                                    className="licence-quota__item quota-link"
                                                >
                                                    {i('license.cta.fixe')}
                                                </Link>
                                            )}
                                        </span>
                                    </React.Fragment>
                                );
                            })}
                </>
            )}
        </div>
    );
};
